import Link from 'next/link';
import styled from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { FILTER_ENUMS } from 'search/enums/constants';
import { IFacetOption } from 'shared/types/ConstructorFacet';
import { ITransformedCategory } from 'search/types/Category';
import { SearchFilterListOption } from 'search/styles/SearchStyles';
import {
  getAllNestedChildrenValues,
  transformCategoryFacet,
} from 'search/utils/categoryFilterUtils';
import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { getCategoryFilterQuery } from 'search/utils/getCategoryFiltersQuery';
import { getCrawlerCategoryFiltersLinkHref } from 'search/utils/getCrawlerCategoryFiltersLinkHref';
import { getCrawlerChildrenCategories } from 'search/utils/getCrawlerChildrenCategories';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { getConstructorCategoriesStrings } from 'search/enums/translations';
import { startCase } from 'shared/utils/startCase';

interface ISearchFilterCategoryDesktop {
  filterOptions: Array<IFacetOption>;
  facetType?: string;
  topLevelCategory: string;
  resultIds: Array<string>;
  isCrawler?: boolean;
}

interface ISearchFilterCategory {
  category: ITransformedCategory;
  categoryFacet: ITransformedCategory;
  resultIds: Array<string>;
  isCrawler?: boolean;
}

interface ICategory {
  category: ITransformedCategory;
  children: React.ReactNode;
  categoryFacet?: ITransformedCategory;
  resultIds: Array<string>;
  isCrawler?: boolean;
}

const Category = ({
  category,
  children,
  categoryFacet,
  resultIds,
  isCrawler = false,
}: ICategory) => {
  const hasChildrenSelected = getAllNestedChildrenValues(category).length > 0;
  const [isExpanded, setIsExpanded] = useState(hasChildrenSelected);
  const { pathname, query } = useRouter();
  const eventTrackingEventContext = useEventContext();

  const toggleFilter = () => {
    setIsExpanded((prevState) => !prevState);

    sendTrackingEvent('FILTER_APPLY_TAP', {
      event_context: eventTrackingEventContext,
      filter_name: FILTER_ENUMS.CATEGORY,
      filter_selection: category.value,
      result_id: resultIds[0],
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query.query.toString() }),
    });
  };

  useEffect(() => {
    // if no categories filter selected, close it
    if (!Object.keys(query).includes('web_groups')) {
      setIsExpanded(false);
    }
  }, [JSON.stringify(category)]);

  return (
    <>
      <Link
        href={
          isCrawler
            ? getCrawlerCategoryFiltersLinkHref({ pathname, query, category, facet: categoryFacet })
            : {
                pathname,
                query: getCategoryFilterQuery(query, category, categoryFacet),
              }
        }
        passHref
        prefetch={false}
        scroll
        shallow
        legacyBehavior
      >
        <SearchFilterListOption
          data-qa={`${category.value}_category_filter`}
          onClick={toggleFilter}
          $isSelected={category.status === 'selected' || (children && isExpanded)}
        >
          {startCase(getConstructorCategoriesStrings(category?.display_name))}
        </SearchFilterListOption>
      </Link>
      {!isCrawler && isExpanded && children && (
        <Category.Collapsible>{children}</Category.Collapsible>
      )}
    </>
  );
};

export const SearchFilterCategory = ({
  category,
  categoryFacet,
  resultIds,
  isCrawler = false,
}: ISearchFilterCategory) => {
  return (
    <Category
      category={category}
      categoryFacet={categoryFacet}
      resultIds={resultIds}
      isCrawler={isCrawler}
    >
      {category?.children?.length > 0 &&
        category.children.map((child) => {
          return (
            <SearchFilterCategory
              category={child}
              categoryFacet={categoryFacet}
              key={child.value}
              resultIds={resultIds}
            />
          );
        })}
    </Category>
  );
};

export const SearchFilterCategoryDesktop = ({
  filterOptions,
  topLevelCategory,
  resultIds,
  isCrawler = false,
}: ISearchFilterCategoryDesktop) => {
  const { query, pathname } = useRouter();
  const transformedCategories = useMemo(
    () => transformCategoryFacet(filterOptions, topLevelCategory),
    [JSON.stringify(filterOptions)],
  );

  const categoriesList = !isCrawler
    ? transformedCategories?.children?.length > 0 && transformedCategories.children
    : getCrawlerChildrenCategories({
        query,
        pathname,
        categories: transformedCategories?.children,
      });

  return (
    <>
      {categoriesList.map((category) => (
        <SearchFilterCategory
          category={category}
          categoryFacet={transformedCategories}
          key={category.value}
          resultIds={resultIds}
          isCrawler={isCrawler}
        />
      ))}
    </>
  );
};

Category.Collapsible = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
`;
